import React from 'react'
import PropTypes from 'prop-types'
import Modal from 'components/mui/Modal'
import Typography from 'components/mui/Typography'
import MButton from 'components/mui/Button'
import { Box, useTheme, alpha, CircularProgress } from '@mui/material'
import { Info as InfoIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, Warning as WarningIcon } from '@mui/icons-material'
import { typography } from 'theme'

// Component scope - Constants and static data
const ICONS = {
  info: InfoIcon,
  success: CheckCircleIcon,
  error: ErrorIcon,
  warning: WarningIcon,
}

export const ConfirmationModel = ({
  open,
  icon,
  onClose,
  onConfirm,
  title,
  iconPropsStyle,
  description,
  confirmText,
  cancelText,
  subtitle,
  showCancelButton = true,
  backgroundColor = '#13172A',
  variant,
  sx,
  modalSx,
  loading = false,
}) => {
  const theme = useTheme()

  // Get icon component - use custom icon if provided, otherwise use variant-based icon
  const IconComponent = icon ? null : (variant && ICONS[variant] ? ICONS[variant] : null)

  // Define icon colors based on variant (only apply if using variant, not custom icon)
  const getIconColor = () => {
    // If custom icon is provided, don't apply variant color (let iconPropsStyle handle it)
    if (icon) return undefined

    // Apply variant-based color
    if (!variant) return theme.palette.common.white
    switch (variant) {
      case 'info':
        return theme.palette.info.main
      case 'success':
        return theme.palette.success.main
      case 'error':
        return theme.palette.error.main
      case 'warning':
        return theme.palette.warning.main
      default:
        return theme.palette.common.white
    }
  }

  const iconColor = getIconColor()

  const modalStyle = {
    padding: { xs: theme.spacing(4), md: theme.spacing(6) },
    width: { xs: '100%', sm: 800, md: 650 },
    // TODO: it shuold be dynamic based on the content
    // minHeight: { xs: 280, md: 320 },
    bgcolor: backgroundColor,
    borderRadius: theme.spacing(1.5),
    // p: { xs: theme.spacing(5), md: theme.spacing(4) },
    ...sx,
  }

  const icondefaultStyle = {
    width: { xs: theme.spacing(6), md: theme.spacing(7.5) },
    height: { xs: theme.spacing(6), md: theme.spacing(7.5) },
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: `0 auto ${theme.spacing(3)} auto`,
    fontSize: { xs: typography.fontSize.h4, md: typography.fontSize.h2 },
    fontWeight: typography.fontWeight.bold,
    '& .MuiSvgIcon-root': {
      fontSize: { xs: typography.fontSize.h4, md: typography.fontSize.h2 },
      ...(iconColor && { color: iconColor }),
    }
  }
  // Extract icon-specific styles from iconPropsStyle to merge separately
  const { '& .MuiSvgIcon-root': iconPropsSvgStyle, ...restIconPropsStyle } = iconPropsStyle || {}

  const iconStyle = {
    ...icondefaultStyle,
    ...restIconPropsStyle,
    // Merge SVG icon styles - iconPropsStyle can override variant color
    '& .MuiSvgIcon-root': {
      ...icondefaultStyle['& .MuiSvgIcon-root'],
      ...iconPropsSvgStyle,
    }
  }

  const titleStyle = {
    color: theme.palette.custom?.text?.heading || theme.palette.common.white,
    fontSize: { xs: typography.fontSize.body1, md: typography.fontSize.subheading1 },
    fontWeight: typography.fontWeight.semibold,
    textAlign: 'center',
    marginBottom: theme.spacing(1.5),
    lineHeight: '1.2',
  }

  const descriptionStyle = {
    color: theme.palette.custom?.text?.paragraph || '#B0B0B0',
    fontSize: { xs: typography.fontSize.helperText, md: typography.fontSize.label },
    textAlign: 'center',
    marginBottom: { xs: theme.spacing(3), md: theme.spacing(4) },
    lineHeight: '1.4',
  }

  const buttonContainerStyle = {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: { xs: theme.spacing(1), md: theme.spacing(1.25) },
    justifyContent: 'center',
  }

  const cancelButtonStyle = {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.common.white}`,
    color: theme.palette.custom?.text?.heading || theme.palette.common.white,
    borderRadius: theme.spacing(1),
    padding: { xs: `${theme.spacing(1)} ${theme.spacing(1.75)}`, md: `${theme.spacing(1.25)} ${theme.spacing(2.25)}` },
    fontSize: { xs: typography.fontSize.helperText, md: typography.fontSize.label },
    fontWeight: typography.fontWeight.medium,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    minWidth: { xs: theme.spacing(15), md: theme.spacing(22.5) },
    width: { xs: 'auto', md: 'auto' },
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.1),
    },
  }

  const confirmButtonStyle = {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.custom?.text?.buttonOnPrimary || theme.palette.common.white,
    borderRadius: theme.spacing(1),
    padding: { xs: `${theme.spacing(1)} ${theme.spacing(1.75)}`, md: `${theme.spacing(1.25)} ${theme.spacing(2.25)}` },
    fontSize: { xs: typography.fontSize.helperText, md: typography.fontSize.label },
    fontWeight: typography.fontWeight.medium,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    minWidth: { xs: theme.spacing(15), md: theme.spacing(22.5) },
    width: { xs: 'auto', md: 'auto' },
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.9),
    },
  }

  return (
    <Modal open={open} onClose={onClose} sx={modalSx}>
      <Box sx={modalStyle}>
        {/* Icon */}
        {(icon || IconComponent) && (
          <Box sx={iconStyle}>
            {icon || (IconComponent && <IconComponent />)}
          </Box>
        )}

        {/* Title */}
        <Typography sx={titleStyle}>
          {title}
        </Typography>

        {/* subtitle */}
        <Typography sx={descriptionStyle}>
          {subtitle}
        </Typography>

        {/* Description */}
        <Typography sx={descriptionStyle}>
          {description}
        </Typography>

        {/* Action buttons */}
        <Box sx={buttonContainerStyle}>
          {showCancelButton && (
            <MButton
              sx={cancelButtonStyle}
              onClick={onClose}
            >
              {cancelText}
            </MButton>
          )}
          <MButton
            sx={confirmButtonStyle}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: theme.palette.custom?.text?.buttonOnPrimary || theme.palette.common.white }} />
                <span>Loading...</span>
              </Box>
            ) : (
              confirmText
            )}
          </MButton>
        </Box>
      </Box>
    </Modal>
  )
}

ConfirmationModel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  confirmText: PropTypes.string.isRequired,
  cancelText: PropTypes.string.isRequired,
  showCancelButton: PropTypes.bool,
  icon: PropTypes.node,
  variant: PropTypes.oneOf(['info', 'success', 'error', 'warning']),
  iconPropsStyle: PropTypes.object,
  backgroundColor: PropTypes.string,
  modalSx: PropTypes.object,
  loading: PropTypes.bool
}

ConfirmationModel.defaultProps = {
  title: "Confirm Action",
  description: "Are you sure you want to proceed?",
  confirmText: "Confirm",
  cancelText: "Cancel",
  showCancelButton: true,
  icon: null,
  loading: false
}

export default ConfirmationModel